export type ScopeLevel = 'customer' | 'project' | 'inventory_unit';
export type RuleMode = 'always_destroy' | 'always_aiu' | 'conditional';
/** removed = 用户主动移除的规则（之后回退到上级规则）；archived = 被新版本替代而归档 */
export type RuleState = 'draft' | 'active' | 'archived' | 'removed';

export interface BaselinePhoto {
  url: string;
  caption?: string;
}

export interface ContentChecklistItem {
  item: string;
  qty: number;
}

/** 外观检查之后的处置 / 内件检查之后的处置，选项见 pages/return-inspection/decisionFlow.ts */
export type AppearanceOkAction = 'resell' | 'aiu' | 'continue';
export type AppearanceBadAction = 'aiu' | 'destroy' | 'continue';
export type ContentOkAction = 'resell' | 'aiu';
export type ContentBadAction = 'aiu' | 'destroy';

/** 一个分支（外观一致 / 外观不一致）的设置；只有 action 为 continue 时才有内件处置 */
export interface FlowBranch {
  action: AppearanceOkAction | AppearanceBadAction;
  content_ok?: ContentOkAction;
  content_bad?: ContentBadAction;
}

export interface DecisionFlow {
  appearance_ok: FlowBranch;
  appearance_bad: FlowBranch;
}

export interface ReturnInspectionRule {
  id: number;
  scope_level: ScopeLevel;
  customer_id: number | null;
  project_id: number | null;
  inventory_unit_id: number | null;
  mode: RuleMode;
  state: RuleState;
  version: number;
  packaging_baseline_photos: BaselinePhoto[];
  packaging_notes: string;
  content_baseline_photos: BaselinePhoto[];
  content_checklist: ContentChecklistItem[];
  content_notes: string;
  /** 外包装为吸塑/塑封：外观按"包装是否完好"判定，且外观一致之后不能继续检查内件 */
  packaging_sealed: boolean;
  /** 按流程初检时，每个检查结果之后的处置；其它模式下为 {} */
  decision_flow: DecisionFlow | Record<string, never>;
  remarks: string;
  created_by_id: number | null;
  updated_by_id: number | null;
  /** 最后一次发布这个版本的人（规则可能被管理员/主账号/子账号先后修改，界面上要能看出是谁） */
  updated_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface ReturnInspectionRulePayload {
  scope_level: ScopeLevel;
  customer_id?: number | null;
  project_id?: number | null;
  inventory_unit_id?: number | null;
  mode: RuleMode;
  packaging_baseline_photos?: BaselinePhoto[];
  packaging_notes?: string;
  packaging_sealed?: boolean;
  decision_flow?: DecisionFlow;
  content_baseline_photos?: BaselinePhoto[];
  content_checklist?: ContentChecklistItem[];
  content_notes?: string;
  remarks?: string;
}

/** 一个库存单元（或项目）实际命中的规则来自哪一级，null 表示三级都没有配置 */
export type RuleSource = 'unit' | 'project' | 'customer' | null;

export interface EffectiveUnitRule {
  inventory_unit_id: number;
  source: RuleSource;
  rule_id: number | null;
  mode: RuleMode | null;
  version: number | null;
}

/** 库存单元的内容项（散货库存单元里装的产品） */
export interface UnitContentItem {
  product_name?: string;
  /** 产品的"客户自定义编码" */
  product_code?: string;
  /** 产品的分拣条码列表（后端已按条码 ID 升序排列，第一个是确定的） */
  sorting_codes?: Array<{ code?: string }>;
  quantity?: number;
}

/** 项目下一个散货库存单元，连同它实际命中的规则来源 */
export interface ProjectUnitRow extends EffectiveUnitRule {
  system_barcode: string;
  items: UnitContentItem[];
  /** 库存单元自设的规则：流程会检查内件，但内件的照片/清单/说明都没填（仓库没有参照物） */
  content_baseline_missing?: boolean;
}

/** 规则来源筛选：none = 三级都没有规则 */
export type SourceFilter = 'unit' | 'project' | 'customer' | 'none';

/** 整个项目的规则覆盖概览（不受搜索/筛选影响）：各来源的库存单元数量 */
export interface CoverageSummary {
  total: number;
  unit: number;
  project: number;
  customer: number;
  none: number;
}

/** 规则维护页的主数据：分页的库存单元 + 覆盖概览 + 通用规则/项目级规则 */
export interface ProjectUnitsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ProjectUnitRow[];
  summary: CoverageSummary;
  project_id: number;
  customer_id: number;
  customer_rule: ReturnInspectionRule | null;
  project_rule: ReturnInspectionRule | null;
  project_effective: { source: 'project' | 'customer' | null; rule: ReturnInspectionRule | null };
}

export const RULE_SOURCE_LABEL: Record<'unit' | 'project' | 'customer', string> = {
  unit: '单元级',
  project: '项目级规则',
  customer: '通用规则',
};

export const RULE_MODE_LABEL: Record<RuleMode, string> = {
  always_destroy: '一律销毁',
  always_aiu: '一律AIU入库',
  conditional: '需要执行初检',
};

export const SCOPE_LEVEL_LABEL: Record<ScopeLevel, string> = {
  // 界面上不叫"客户级"：容易被误解成"某个客户的规则"，其实是该客户所有项目共用的默认规则
  customer: '通用',
  project: '项目级',
  inventory_unit: '库存单元级',
};

/** 批量设置规则（复制策略）：目标单元用明确的 ID 列表，或者"当前筛选结果的全部"二选一 */
export interface CopyToUnitsPayload {
  source_rule_id: number;
  inventory_unit_ids?: number[];
  select_filtered?: boolean;
  source?: SourceFilter;
  search?: string;
  overwrite?: boolean;
}

export interface CopyToUnitsProblem {
  inventory_unit_id: number;
  system_barcode: string;
  reason: string;
}

export interface CopyToUnitsResult {
  created: number[];
  skipped: CopyToUnitsProblem[];
  failed: CopyToUnitsProblem[];
  /** 复制出来的规则里，流程会检查内件、但内件基准资料一律为空的数量 */
  content_baseline_missing: number;
}

// ---------------------------------------------------------------------------
// 退货收货核对 / 收货异常（仓库入库扫描，见设计文档 5.3）
// ---------------------------------------------------------------------------

export type ReceivingReason = 'quantity_mismatch' | 'unforecast_items';

export const RECEIVING_REASON_LABEL: Record<ReceivingReason, string> = {
  quantity_mismatch: '数量不符',
  unforecast_items: '预报外物品',
};

/** 异常状态：待客户方案 → 方案已确认 → 已处理（已按方案执行）；已撤销（重新清点） */
export type ReceivingStatus = 'open' | 'plan_ready' | 'resolved' | 'cancelled';

export const RECEIVING_STATUS_LABEL: Record<ReceivingStatus, string> = {
  open: '待客户方案',
  plan_ready: '方案已确认',
  resolved: '已处理',
  cancelled: '已撤销',
};

/** 对实收的每一项货品可选的处理方式 */
export type HandlingMode = 'destroy' | 'aiu' | 'normal' | 'inspect';

export const HANDLING_MODE_LABEL: Record<HandlingMode, string> = {
  destroy: '销毁',
  aiu: '作为AIU入库',
  normal: '作为正常库存入库（不检查）',
  inspect: '按预设规则初检',
};

export const HANDLING_MODE_HINT: Record<HandlingMode, string> = {
  destroy: '不可逆：直接销毁，不入库，会产生销毁费用',
  aiu: '不检查，直接作为异常库存（AIU）入库',
  normal: '不检查，直接作为正常库存入库，可售',
  inspect: '按该库存单元当前生效的规则逐件检查外观（必要时内件），再按规则决定销毁 / 异常品入库 / 正常品入库',
};

/** 要选处理方式的项目：实收数量大于 0 的预报行，或一件预报外物品；数量以异常快照为准，客户不填 */
export interface ReceivingPlanSlot {
  key: string; // line:<库存单元id> | extra:<序号>
  source: 'forecast_line' | 'extra';
  inventory_unit: number | null;
  description: string;
  quantity: number;
}

export interface ReceivingPlanEntry extends ReceivingPlanSlot {
  mode: HandlingMode;
  inventory_unit_barcode?: string | null;
}

export interface ReceivingPlanItemInput {
  key: string;
  mode: HandlingMode;
  inventory_unit?: number | null;
}

export interface ReceivingPlanHistoryEntry {
  plan: ReceivingPlanEntry[];
  origin: string;
  note: string;
  by: string | null;
  at: string | null;
}

export interface ReceivingRowInput {
  inventory_unit: number;
  actual_quantity: number;
}

export interface ReceivingExtraInput {
  description: string;
  quantity: number;
}

export interface ReceivingCheckRow {
  inventory_unit: number;
  inventory_unit_barcode: string;
  unit_label: string;
  forecast_quantity: number;
  actual_quantity: number;
  quantity_ok: boolean;
  ok: boolean;
}

export interface ReceivingCheckResult {
  consistent: boolean;
  reasons: ReceivingReason[];
  rows: ReceivingCheckRow[];
  extras: ReceivingExtraInput[];
  forecast_total: number;
  actual_total: number;
}

export interface ReceivingException {
  id: number;
  forecast: number;
  forecast_code: string;
  status: ReceivingStatus;
  status_display: string;
  reasons: ReceivingReason[];
  reason_labels: string[];
  snapshot: {
    rows: ReceivingCheckRow[];
    extras: ReceivingExtraInput[];
    forecast_total: number;
    actual_total: number;
  };
  photos: string[];
  remarks: string;
  created_at: string;
  created_by_name: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  resolution: 'executed' | 'recheck' | '';
  resolution_display: string;
  resolution_note: string;
  slots: ReceivingPlanSlot[];
  plan: ReceivingPlanEntry[];
  plan_origin: 'customer' | 'warehouse' | '';
  plan_note: string;
  plan_acknowledged: boolean;
  plan_confirmed_at: string | null;
  plan_confirmed_by_name: string | null;
  plan_history: ReceivingPlanHistoryEntry[];
}

// ---------------------------------------------------------------------------
// 执行结果（退货入库初检）
// ---------------------------------------------------------------------------

export interface ExecutionPiece {
  index: number;
  appearance: boolean;
  content: boolean | null;
  content_checked: boolean;
  result: 'resell' | 'aiu' | 'destroy';
}

export interface ExecutionLine {
  id: number;
  source_type: 'forecast_line' | 'extra';
  description: string;
  inventory_unit: number | null;
  inventory_unit_barcode: string | null;
  inventory_unit_remarks: string | null;
  /** 库存单元内容物（只在详情里有）：产品名称 / 客户自定义编码 / 全部分拣条码 / 数量 */
  inventory_unit_contents?: Array<{ product_name: string; product_code: string; sorting_codes: string[]; quantity: number }>;
  mode: HandlingMode;
  mode_display: string;
  quantity: number;
  destroy_qty: number;
  aiu_qty: number;
  normal_qty: number;
  inspected_count: number;
  appearance_checked_count: number;
  content_checked_count: number;
  rule_version: number | null;
  rule_snapshot: Record<string, any>;
  pieces?: ExecutionPiece[];
}

export interface InspectionExecution {
  id: number;
  forecast: number;
  source: 'normal' | 'exception_plan';
  source_display: string;
  inbound_record: number | null;
  inbound_record_code: string | null;
  received_total: number;
  destroy_qty: number;
  aiu_qty: number;
  normal_qty: number;
  inspected_count: number;
  appearance_checked_count: number;
  content_checked_count: number;
  aiu_total_weight_g: number | null;
  /** 销毁总重量（克），销毁费按它收取；没有销毁的件时为空 */
  destroy_total_weight_g: number | null;
  photos: string[];
  remarks: string;
  created_at: string;
  lines: ExecutionLine[];
  /** 入库记录里实际入库的库存单元（正常入库的 LIU 和 AIU），含分拣组，用来加入分拣看板 */
  inbound_items: ExecutionInboundItem[];
}

export interface ExecutionInboundItem {
  inventory_unit: number;
  inventory_unit_barcode: string;
  unit_type: string;
  unit_name: string;
  quantity: number;
  sorting_group_name: string;
  sorting_group_system_code: string;
  sorting_barcodes: string[];
}

/** 仓库初检时对照用的规则内容（只读） */
export interface ReceivingRule {
  id: number;
  scope_level: ScopeLevel;
  mode: RuleMode;
  version: number;
  packaging_sealed: boolean;
  decision_flow: DecisionFlow | Record<string, never>;
  packaging_baseline_photos: BaselinePhoto[];
  packaging_notes: string;
  content_baseline_photos: BaselinePhoto[];
  content_checklist: ContentChecklistItem[];
  content_notes: string;
}

/** 条件判定模式逐件检查的答案：content 只在流程要求检查内件时填写 */
export interface PieceAnswer {
  appearance: boolean;
  content?: boolean | null;
}

